import * as React from 'react';
import { useParams } from 'react-router-dom';
import useGetRequest from 'hooks/useGetRequest';
import styled from 'styled-components';
import { sizes, theme } from 'theme';
import { BodyRegular, Headline2 } from 'components/Elements';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import strings from '../../strings/strings.json';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import { OrganisationPublic } from '../../models/OrganisationPublic';
import YourTrainerCard from 'pages/learner/components/YourTrainerCard';
import { CourseCardsContainer, LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import ExpandableContentComponent from 'components/ExpandableContentComponent';
import CourseCardWithColouredHeader from 'components/courseCards/CourseCardWithColouredHeader';
import Header from 'components/sections/Header';
import { ContentBlock, PublicPage } from './PublicCoursePage';
import { AuthContext } from 'contextProviders/AuthContext';
import { RoleType } from 'models/User';

export default function PublicOrganisationPage(): JSX.Element {
    const { isAuthenticated, userData } = React.useContext(AuthContext);
    const params = useParams();
    const { name } = params;
    const {
        data: organisation,
        loading,
        errors,
    } = useGetRequest<OrganisationPublic>(`public/organisations/${name}`, undefined, true);

    if (loading || !organisation) {
        return <></>;
    }

    const userRole = isAuthenticated ? userData.activeRole : undefined;
    let cardHeaderColour = `${theme.learnerHeaderTextBackground}`;

    if (userRole) {
        switch (userRole) {
            case RoleType.TRAINER:
                cardHeaderColour = `${theme.trainerHeaderTextBackground}`;
                break;
            case RoleType.ORGANISATION_ADMIN:
                cardHeaderColour = `${theme.orgHeaderTextBackground}`;
                break;
            default:
            case RoleType.ADMIN:
            case RoleType.LEARNER:
                break;
        }
    }

    if (errors.length > 0) {
        return (
            <MainContent>
                <NotificationContainer>
                    <ErrorNotifier
                        title={strings.publicOrganisationPage.orgNotFoundError.title}
                        description={strings.publicOrganisationPage.orgNotFoundError.description}
                    />
                </NotificationContainer>
            </MainContent>
        );
    }

    return (
        <>
            <Header logoS3Filepath={organisation.logoUrl} title={organisation.name} textColourInverted />

            <PublicPage>
                {organisation.description && (
                    <ContentBlock id="org-content-section">
                        <Headline2>About</Headline2>
                        <OrganisationDescription id="org-description">
                            <BodyRegular>{organisation?.description}</BodyRegular>
                        </OrganisationDescription>
                    </ContentBlock>
                )}

                <ContentBlock id="courses-content-section">
                    <Headline2>{strings.publicOrganisationPage.courses}</Headline2>

                    {organisation.courses.length > 0 ? (
                        <CourseCardsContainer id="courses-grid">
                            {organisation.courses.map((courseOverview, index) => (
                                <CourseCardWithColouredHeader
                                    key={index}
                                    courseOverview={courseOverview}
                                    cardHeaderColour={cardHeaderColour}
                                />
                            ))}
                        </CourseCardsContainer>
                    ) : (
                        <NotificationContainer>
                            <Notification
                                notificationType={NotificationType.INFO}
                                title={strings.publicOrganisationPage.organisationHasNoCourses}
                            />
                        </NotificationContainer>
                    )}
                </ContentBlock>

                {organisation.trainers.length > 0 && (
                    <ContentBlock id="trainers-content-section">
                        <Headline2>{strings.publicOrganisationPage.trainers}</Headline2>
                        {organisation.trainers.map((trainer, index) => (
                            <YourTrainerCard
                                key={index}
                                trainerName={`${trainer.firstName} ${trainer.lastName}`}
                                trainerBio={trainer.bio}
                                profileImageFilepath={
                                    trainer.profileImage ? `${DOCUMENTS_URL}/${trainer.profileImage}` : ''
                                }
                            />
                        ))}
                    </ContentBlock>
                )}

                {organisation.pastCourses.length > 0 && (
                    <ContentBlock id="past-courses-section-content">
                        <ExpandableContentComponent
                            headline={strings.publicOrganisationPage.pastCourses}
                            closedHeightInPx={0}
                            content={
                                <CourseCardsContainer id="past-courses-grid">
                                    {organisation.pastCourses.map((courseOverview, index) => (
                                        <CourseCardWithColouredHeader
                                            key={index}
                                            courseOverview={courseOverview}
                                            cardHeaderColour={cardHeaderColour}
                                        />
                                    ))}
                                </CourseCardsContainer>
                            }
                        />
                    </ContentBlock>
                )}
            </PublicPage>
        </>
    );
}

const MainContent = styled.div`
    padding: ${sizes.spacingLg} ${sizes.edgePadding};
    width: 100%;
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
    display: flex;
    flex-direction: column;
    align-self: center;
    gap: ${sizes.spacingLg};
`;

const OrganisationDescription = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
    width: 100%;
`;

const NotificationContainer = styled.div`
    display: flex;
    align-items: start;
`;
